import React, { useEffect, useRef, useState } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  Button, Box, CircularProgress, Typography, IconButton 
} from '@mui/material';
import { Close, MyLocation } from '@mui/icons-material';

const MapPicker = ({ open, onClose, onSelect, initialLocation }) => {
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [addressPreview, setAddressPreview] = useState('');
  const [selectedCoords, setSelectedCoords] = useState(initialLocation || { lat: 13.0827, lng: 80.2707 }); // Default Chennai

  useEffect(() => {
    if (open && window.L) {
      setTimeout(() => {
        if (!mapRef.current) {
          mapRef.current = window.L.map('map-container').setView([selectedCoords.lat, selectedCoords.lng], 13);
          window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '© OpenStreetMap'
          }).addTo(mapRef.current);

          markerRef.current = window.L.marker([selectedCoords.lat, selectedCoords.lng], { draggable: true }).addTo(mapRef.current);

          mapRef.current.on('click', (e) => {
            const { lat, lng } = e.latlng;
            updateLocation(lat, lng);
          });

          markerRef.current.on('dragend', (e) => {
            const { lat, lng } = e.target.getLatLng();
            updateLocation(lat, lng);
          });
          
          // Initial geocode
          reverseGeocode(selectedCoords.lat, selectedCoords.lng);
        } else {
          mapRef.current.invalidateSize();
        }
      }, 100);
    }
    
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [open]);

  const updateLocation = (lat, lng) => {
    setSelectedCoords({ lat, lng });
    if (markerRef.current) {
      markerRef.current.setLatLng([lat, lng]);
    }
    reverseGeocode(lat, lng);
  };

  const reverseGeocode = async (lat, lng) => {
    setLoading(true);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`);
      const data = await res.json();
      if (data && data.display_name) {
        setAddressPreview(data.display_name);
        // Extract useful parts for the form
        const addr = data.address;
        data.extracted = {
          addressLine1: addr.road || addr.suburb || addr.neighbourhood || '',
          city: addr.city || addr.town || addr.village || addr.county || '',
          state: addr.state || '',
          pincode: addr.postcode || '',
          landmark: addr.amenity || addr.landmark || addr.building || '',
        };
        setSelectedCoords(prev => ({ ...prev, details: data }));
      }
    } catch (e) {
      console.error("Geocoding failed", e);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    onSelect(selectedCoords);
    onClose();
  };

  const handleGetCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((pos) => {
        const { latitude, longitude } = pos.coords;
        if (mapRef.current) {
          mapRef.current.setView([latitude, longitude], 16);
          updateLocation(latitude, longitude);
        }
      }, (err) => {
        alert("Unable to retrieve your location");
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Select Location on Map
        <IconButton onClick={onClose} size="small"><Close /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, position: 'relative' }}>
        <Box id="map-container" sx={{ height: '400px', width: '100%' }} />
        <Button 
          variant="contained" 
          startIcon={<MyLocation />}
          onClick={handleGetCurrentLocation}
          sx={{ 
            position: 'absolute', bottom: 20, right: 20, zIndex: 1000,
            bgcolor: '#fff', color: '#2874f0', '&:hover': { bgcolor: '#f1f3f6' }
          }}
        >
          Locate Me
        </Button>
      </DialogContent>
      <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', p: 2, gap: 1 }}>
        <Box sx={{ minHeight: '40px', mb: 1 }}>
          {loading ? <CircularProgress size={20} /> : (
            <Typography variant="body2" color="text.secondary">
              {addressPreview || "Click on map to select location"}
            </Typography>
          )}
        </Box>
        <Button 
          fullWidth 
          variant="contained" 
          onClick={handleConfirm}
          disabled={!addressPreview || loading}
          sx={{ bgcolor: '#2874f0', color: '#fff' }}
        >
          Confirm Location
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MapPicker;
